import { PARegistry } from "../singletons/pa-registry";
import * as mapboxgl from "mapbox-gl";
import type { PAStore, StoreGeometry, StoreLetterProperties, StoreProperties } from "./store";
import type { EventData, GeoJSONSource, MapboxGeoJSONFeature, MapMouseEvent } from "mapbox-gl";
import type { DynamicComponentService } from "../../../_services/dynamic-component-service";
import { StorePopupComponent } from "../reuseable-components/store-popup/store-popup.component";
import type { PATechnicianDate } from "./technician-date";
import { PAStoreControl } from "../singletons/pa-store-control";
import type { MapContainer } from "../map/pa-map";
import type { PAOperation } from "./operation";
export interface StoreClustererInput {stores: PAStore[], technician_date?: PATechnicianDate}

export class PAMapStoreClusterer {

  private _input: StoreClustererInput = {stores: [], technician_date: null}
  private _clusteredStoreFeatures: GeoJSON.Feature<StoreGeometry, StoreProperties>[] = []
  private _unclusteredStoreFeatures: GeoJSON.Feature<StoreGeometry, StoreProperties>[] = []
  private _storeLetterFeatures: GeoJSON.Feature<StoreGeometry, StoreLetterProperties>[] = []

  constructor(
    public mapContainer: MapContainer,
    private dynamicComponentService: DynamicComponentService,
  ) {
    this.initMapLayers()
  }

  private initMapLayers(): void {
    this.mapContainer.map.addSource('clustered_stores', {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: this._clusteredStoreFeatures
      },
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 30
    })

    this.mapContainer.map.addSource('store_letters', {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: this._storeLetterFeatures
      },
    })

    this.mapContainer.map.addSource('unclustered_stores', {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: this._unclusteredStoreFeatures
      },
    })

    this.mapContainer.map.addLayer({
      id: 'store_clusters',
      type: "circle",
      source: 'clustered_stores',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#51bbd6',
          50,
          '#f1f075',
          200,
          '#f28cb1'
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          25,
          100,
          35,
          750,
          45
        ],
        "circle-stroke-width": 1,
        "circle-stroke-color": '#000',
        "circle-opacity": .8
      }
    })

    this.mapContainer.map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'clustered_stores',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
        "text-allow-overlap": false
      }
    });

    this.mapContainer.map.addLayer({
      id: 'point',
      type: 'circle',
      source: 'unclustered_stores',
      paint: {
        'circle-color': ['get', 'color'],
        'circle-radius': 20,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#16b7dc',
      }
    });

    this.mapContainer.map.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'clustered_stores',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': ['get', 'color'],
        'circle-radius': 20,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#000',
      }
    });

    this.mapContainer.map.addLayer({
      'id': 'unclustered-clabel',
      'type': 'symbol',
      'source': 'clustered_stores',
      'filter': ['!', ['has', 'point_count']],
      'layout': {
        'text-field': ['get', 'short_name'],
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': 11,
        'text-allow-overlap': true
      },
      'paint': {
        'text-color': '#000',
      }
    });

    this.mapContainer.map.addLayer({
      'id': 'unclustered_time_label',
      'type': 'symbol',
      'source': 'clustered_stores',
      'filter': ['!', ['has', 'point_count']],
      'layout': {
        'text-field': ['get', 'next_addable_operation_time'],
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': 11,
        'text-allow-overlap': true,
        'text-offset': [0, 2.5]
      },
      'paint': {
        'text-color': '#000',
      }
    });

    this.mapContainer.map.addLayer({
      'id': 'clabel',
      'type': 'symbol',
      'source': 'unclustered_stores',
      'layout': {
        'text-field': ['get', 'short_name'],
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': 10,
        'text-allow-overlap': true,
        'text-offset': [0, 1]
      },
      'paint': {
        'text-color': '#000',
      }
    });

    const controls_radius = 1.1
    const circle_radius = 10
    const rad_factor = Math.PI / 180

    /*this.mapContainer.map.addLayer({
      id: 'store-operation-count-circle',
      type: 'circle',
      source: 'clustered_stores',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': 'rgba(80,80,80,0.6)',
        'circle-radius': .9 * circle_radius,
        "circle-translate": [circle_radius * controls_radius * Math.cos(205 * rad_factor), circle_radius * controls_radius * Math.sin(205 * rad_factor)],
      }
    });*/

    /*this.mapContainer.map.addLayer({
      'id': 'store-operation-count-clabel',
      'type': 'symbol',
      'source': 'clustered_stores',
      'filter': ['!', ['has', 'point_count']],
      'layout': {
        'text-field': ['get', 'done_operations'],
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': 10,
        "text-offset": [controls_radius * Math.cos(205 * rad_factor), controls_radius * Math.sin(205 * rad_factor)],
        'text-allow-overlap': true
      },
      'paint': {
        'text-color': ['get', 'done_operations_color'],
      }
    });*/

    const change_operations_label_size_factor = 1.5
    /*this.map.addLayer({
      id: 'store-remove-operation-circle',
      type: 'circle',
      source: 'clustered_stores',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': 'rgba(80,80,80,0.7)',
        'circle-radius': circle_radius,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#fff',
        "circle-translate": [circle_radius * controls_radius * Math.cos(162 * rad_factor), circle_radius * controls_radius * Math.sin(162 * rad_factor)],
      }
    });

    this.map.addLayer({
      'id': 'store-remove-operation-label',
      'type': 'symbol',
      'source': 'clustered_stores',
      'filter': ['!', ['has', 'point_count']],
      'layout': {
        'text-field': '−',
        'text-size': circle_radius * change_operations_label_size_factor,
        "text-offset": [controls_radius * Math.cos(162 * rad_factor) / change_operations_label_size_factor, controls_radius * Math.sin(162 * rad_factor) / change_operations_label_size_factor],
        'text-allow-overlap': true
      },
      'paint': {
        'text-color': '#fff',
      }
    });*/

    /*this.mapContainer.map.addLayer({
      id: 'store-add-operation-circle',
      type: 'circle',
      source: 'clustered_stores',
      filter: ['all', ['!', ['has', 'point_count']], ['get', 'show_add_operation_button']],
      paint: {
        'circle-color': 'rgba(80,80,80,0.9)',
        'circle-radius': circle_radius,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#fff',
        "circle-translate": [2 * circle_radius * controls_radius * Math.cos(0 * rad_factor), 2 * circle_radius * controls_radius * Math.sin(0 * rad_factor)],
      }
    });*/

    /*this.mapContainer.map.addLayer({
      'id': 'store-add-operation-clabel',
      'type': 'symbol',
      'source': 'clustered_stores',
      'filter': ['all', ['!', ['has', 'point_count']], ['get', 'show_add_operation_button']],
      'layout': {
        'text-field': '+',
        'text-size': change_operations_label_size_factor * circle_radius,
        "text-offset": [2 * controls_radius * Math.cos(0 * rad_factor) / change_operations_label_size_factor, 2 * controls_radius * Math.sin(0 * rad_factor) / change_operations_label_size_factor],
        'text-allow-overlap': true
      },
      'paint': {
        'text-color': '#fff',
      }
    });*/

    this.mapContainer.map.addLayer({
      id: 'store-letter-circle',
      type: 'circle',
      source: 'store_letters',
      paint: {
        'circle-color': '#673ab7',
        'circle-radius': .8 * circle_radius,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#fff',
        "circle-translate": [circle_radius * controls_radius * Math.cos(335 * rad_factor), circle_radius * controls_radius * Math.sin(335 * rad_factor)],
      }
    });

    this.mapContainer.map.addLayer({
      'id': 'store-letter-clabel',
      'type': 'symbol',
      'source': 'store_letters',
      'layout': {
        'text-field': ['get', 'letter'],
        'text-size': circle_radius,
        "text-offset": [controls_radius * Math.cos(335 * rad_factor), controls_radius * Math.sin(335 * rad_factor)],
        'text-allow-overlap': true
      },
      'paint': {
        'text-color': '#fff',
      }
    });

    /*this.mapContainer.map.addLayer({
      'id': 'next-addable-operation-time-clabel',
      'type': 'symbol',
      'source': 'stores',
      'filter': ['all', ['!', ['has', 'point_count']], ['get', 'show_add_operation_button']],
      'layout': {
        'text-field': ['get', 'next_addable_operation_time'],
        'text-size': circle_radius,
        "text-offset": [2.5 * controls_radius * Math.cos(90 * rad_factor), 2.5 * controls_radius * Math.sin(90 * rad_factor)],
        'text-allow-overlap': true
      },
      'paint': {
        'text-color': 'black',
      }
    });*/

    const clustered_stores_clicker = new CustomDBLClicker<MapMouseEvent & {features?: MapboxGeoJSONFeature[]} & EventData>(
      (e) => {
        const features = this.mapContainer.map.queryRenderedFeatures(e.point, {
          layers: ['store_clusters']
        });
        const clusterId = features[0].properties.cluster_id;
        const source = this.mapContainer.map.getSource('clustered_stores') as GeoJSONSource
        source.getClusterExpansionZoom(
          clusterId,
          (err, zoom) => {
            if (err) return;

            this.mapContainer.map.easeTo({
              center: features[0].geometry['coordinates'],
              zoom: zoom
            });
          }
        );
      },
      (e) => {
        const features = this.mapContainer.map.queryRenderedFeatures(e.point, {
          layers: ['store_clusters']
        });
        const clusterId = features[0].properties.cluster_id,
        point_count = features[0].properties.point_count,
        clusterSource = this.mapContainer.map.getSource('clustered_stores') as GeoJSONSource

        // Get all points under a cluster
        clusterSource.getClusterLeaves(clusterId, point_count, 0, (err, aFeatures) => {
          let add_operations: PAOperation[] = []
          aFeatures.map(f => {
            const store = PARegistry.dataControl.getStore(f.properties.id)
            if (this.input?.technician_date) {
              add_operations = add_operations.concat(store.getDoableOperationsForTechnicianDate(this.input.technician_date))
            } else {
              add_operations = add_operations.concat(store.getUnassignedOperations())
            }
          })
          PARegistry.tourPlannerControl.addOperationsToTourPlanning(add_operations)
          if (this.input?.technician_date) {
            PARegistry.tourPlannerControl.planningInstructions.addOperationsTechnicianConstraints(add_operations, [this.input.technician_date.technician], [this.input.technician_date.day.utc_timestamp])
            PARegistry.tourPlannerControl.planningInstructions.addOperationsTechnicianConstraints(add_operations, [this.input.technician_date.technician], [this.input.technician_date.day.utc_timestamp])
          }
        })
      }
    )

    this.mapContainer.map.on('click', 'store_clusters', e => clustered_stores_clicker.click(e))

    this.mapContainer.map.on('mouseenter', 'store_clusters', () => {
      this.mapContainer.map.getCanvas().style.cursor = 'pointer';
    });
    this.mapContainer.map.on('mouseleave', 'store_clusters', () => {
      this.mapContainer.map.getCanvas().style.cursor = '';
    });
    this.mapContainer.map.on('mouseenter', 'unclustered_time_label', (e) => {

      const tooltip_text = e.features[0]?.properties.operation_time_tooltip
      if (tooltip_text) {

        this.mapContainer.map.getCanvas().style.cursor = 'pointer';
        const popup = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: true,
          anchor: "top",
          offset: 30
        })
        const coordinates = e.features[0].geometry.type == 'Point' ? e.features[0].geometry.coordinates : null;
        const tooltip = `<div>${tooltip_text}</div>`;

        popup.setLngLat([coordinates[0], coordinates[1]]).setHTML(tooltip).addTo(this.mapContainer.map);

        this.mapContainer.map.on('mouseleave', 'unclustered_time_label', (e) => {
          this.mapContainer.map.getCanvas().style.cursor = '';
          popup.remove();
        });
      }
    });

    const unclustered_store_clicker = new CustomDBLClicker<MapMouseEvent & EventData>(
      (e) => {
        const f = this.mapContainer.map.queryRenderedFeatures(e.point, {layers: ['unclustered-point', 'point']});
        if (f.length) {
          this.clickUnclusteredStore(f, e.lngLat);
          return;
        }
      },
      (e) => {
        const f = this.mapContainer.map.queryRenderedFeatures(e.point, {layers: ['unclustered-point', 'point']});
        if (f.length) {
          this.clickAddStoreOperationButton(f);
          return;
        }
      }
    )

    this.mapContainer.map.on('click', e => unclustered_store_clicker.click(e));
  }

  private clickAddStoreOperationButton(features: mapboxgl.MapboxGeoJSONFeature[]) {
    const store_id = features[0].properties.id
    const store = PARegistry.dataControl.getStore(store_id)
    store.addNextDoableUnplannedOperationToTourPlanning(this._input.technician_date)
  }

  private clickUnclusteredStore(features: mapboxgl.MapboxGeoJSONFeature[], lng_lat: mapboxgl.LngLat) {
    const coordinates = features[0].geometry['coordinates'].slice();

    while (Math.abs(lng_lat.lng - coordinates[0]) > 180) {
      coordinates[0] += lng_lat.lng > coordinates[0] ? 360 : -360;
    }

    const store = PARegistry.dataControl.getStore(features[0].properties.id)
    const projects = [...new Set(store.shownMapOperations.map(op => op.ticket.project))]

    const injected_component = this.dynamicComponentService.injectComponent(StorePopupComponent, x => {
      x.store = store
      x.projects = projects
      x.expandedProjects = projects
      x.technicianDate = this._input.technician_date
    })

    const popup = new mapboxgl.Popup({'offset': 20, 'maxWidth': '400px'})
      .setLngLat(coordinates)
      .setDOMContent(injected_component.div_element)
      .addTo(this.mapContainer.map);
    PAStoreControl.Instance.selectStore(store)

    popup.on('close', function (_) {
      injected_component.component_ref.destroy()
    })
  }

  get input(): StoreClustererInput {
    return this._input
  }

  set input(input: StoreClustererInput) {
    this._input = input

    const unclustered_stores = input.stores.filter(s => s.letter)
    const skip_letter_update = this._unclusteredStoreFeatures.every(f => unclustered_stores.find(s => s.id == f.id)) && unclustered_stores.every(s => this._unclusteredStoreFeatures.find(f=> f.id == s.id))

    this._unclusteredStoreFeatures = unclustered_stores.map(s => s.toMapboxStoreFeature({reference_td: this._input.technician_date, hide_operations_time: true}))
    this._storeLetterFeatures = unclustered_stores.filter(s => s.letter).map(s => s.toMapboxStoreLetterFeature())
    this._clusteredStoreFeatures = input.stores.filter(s => !s.letter).map(s => s.toMapboxStoreFeature({reference_td: this._input.technician_date}))
    this.updateFeatures({skip_letters: skip_letter_update})
  }

  updateFeatures(config?: {skip_letters?: boolean}) {
    const clustered_stores_source = this.mapContainer.map.getSource('clustered_stores') as GeoJSONSource
    const unclustered_stores_source = this.mapContainer.map.getSource('unclustered_stores') as GeoJSONSource
    const store_letters_source = this.mapContainer.map.getSource('store_letters') as GeoJSONSource
    console.log(`updating store cluster features for ${this.mapContainer.name}`)
    if (clustered_stores_source) clustered_stores_source.setData({
      type: 'FeatureCollection',
      features: this._clusteredStoreFeatures
    })

    if (unclustered_stores_source) unclustered_stores_source.setData({
      type: 'FeatureCollection',
      features: this._unclusteredStoreFeatures
    })

    if (store_letters_source && !config?.skip_letters) store_letters_source.setData({
      type: 'FeatureCollection',
      features: this._storeLetterFeatures
    })
  }

  findStoreFeaturesBetweenCoordinates(start_coords: mapboxgl.LngLat, end_coords: mapboxgl.LngLat): GeoJSON.Feature<StoreGeometry, StoreProperties>[] {
    return this._clusteredStoreFeatures.concat(this._unclusteredStoreFeatures).filter(feature => {
      const feature_coords = feature.geometry.coordinates
      const lat_matches = start_coords.lat < end_coords.lat ? (start_coords.lat <= feature_coords[1] && feature_coords[1] <= end_coords.lat) : (start_coords.lat >= feature_coords[1] && feature_coords[1] >= end_coords.lat)
      const lng_matches = start_coords.lng < end_coords.lng ? (start_coords.lng <= feature_coords[0] && feature_coords[0] <= end_coords.lng) : (start_coords.lng >= feature_coords[0] && feature_coords[0] >= end_coords.lng)
      return lat_matches && lng_matches
    })
  }
}

export class CustomDBLClicker<IN> {

  private _pendingClick: number | undefined;
  private _clicked = 0;

  constructor(
    public singleClick: ((arg0: IN) => void),
    public doubleClick: ((arg0: IN) => void),
    private _timeDBClick = 500
  ) {
  }

  click(arg0: IN):void {
    this._clicked++;
    window.clearTimeout(this._pendingClick)
    if(this._clicked >= 2){
      this.doubleClick(arg0)
      this._clicked = 0;
    } else {
      this._pendingClick = window.setTimeout(() => {
        this.singleClick(arg0)
        this._clicked = 0;
      }, this._timeDBClick);
    }
  }

}
